import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "A JavaScript function that makes $40,000+/year",
  "description": "What's the best code you ever wrote? Is it the most clever, or the cleanest. The most fun problem to solve, or the most impactful. The surprise throwaway everyone uses, or the love nobody noticed.",
  "published": "2020-09-21T00:00:00.000Z",
  "hero": "./img/screenshot-1600702224141.png"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`Friend, what's the best code you ever wrote?`}</p>
    <p>{`You can think of the most clever code you ever wrote, or the cleanest. The most fun problem to solve, or the most impactful. The surprise throwaway everyone uses, or the love nobody noticed.`}</p>
    <p>{`Like that time `}<a parentName="p" {...{
        "href": "https://qz.com/646467/how-one-programmer-broke-the-internet-by-deleting-a-tiny-piece-of-code/"
      }}>{`deleting 11 lines of left-pad code`}</a>{` broke the internet. Talk about little code big impact 😅`}</p>
    <p>{`For me, it's a toss-up between 2 projects.`}</p>
    <h2 {...{
      "id": "the-coolest-piece-of-code"
    }}>{`The coolest piece of code`}</h2>
    <p>{`The most fun, clever, and whoa, was a 1500+ line PHP function I wrote back in high school. It was horrible, hard to maintain, kinda slow, and amazing as heck.`}</p>
    <p>{`A compiler.`}</p>
    <p>{`This gigantic-ass method could take a file written in a custom templating language – inspired by Django and Smarty – and turn it into PHP. It replaced the interpreter from the first version of my templating engine.`}</p>
    <p>{`And the results were amazing. Pages loaded 10x faster than with the interpreter. Even without caching.`}</p>
    <p>{`Yep, it was 10x faster to compile code on every request than it was to interpret (execute step by step).`}</p>
    <p><video parentName="p" {...{
        "style": {
          "margin": "auto auto",
          "display": "block",
          "maxWidth": "80%"
        },
        "autoPlay": true,
        "loop": true,
        "muted": true,
        "playsInline": true,
        "loading": "lazy"
      }}>{`
            `}<source parentName="video" {...{
          "src": "https://media4.giphy.com/media/QWw4hc5gTnJhY0BUI3/giphy-loop.mp4?cid=7cf28b0fm0spqye9yivo0m9wev6so3lq3psmepf2ehi5r2ko&rid=giphy-loop.mp4&ct=g",
          "type": "video/mp4"
        }}></source>{`
        `}</video></p>
    <p>{`My custom language supported variables, loops, nested loops, conditionals, and even functions. All before I learned anything about how compilers, parsers, or interpreters work.`}</p>
    <p>{`Wrote it over Christmas break one year. A 1500 line switch statement. ✌️`}</p>
    <h2 {...{
      "id": "the-best-piece-of-code"
    }}>{`The `}<em parentName="h2">{`best`}</em>{` piece of code`}</h2>
    <p>{`While impressive for an 18 year old, my little compiler never accomplished much. Supported 2 or 3 websites in production and caused months of debugging headaches.`}</p>
    <p>{`Here's a piece of code far more impactful:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-typescript"
      }}>{`export const pingHandler = async (
  event: APIGatewayEvent
): Promise<APIResponse> => {
  const ping: GumroadPing = qs.parse(event.body!) as any;

  console.log("ping for", ping.product_permalink);

  if (ping.product_permalink in PRODUCTS) {
    const user = await upsertUser(ping);

    if (user) {
      const auth0 = await getAuth0Client();
      const roleId = PRODUCTS[ping.product_permalink];

      await auth0.assignRolestoUser(
        { id: user.user_id! },
        {
          roles: [roleId],
        }
      );
    }
  }

  return response(200, {});
};
`}</code></pre>
    <p>{`Since going live in January, that code has made over $40,000 in revenue, cost $2 to run, and took 2 hours to write while reading docs.`}</p>
    <p>{`Typical glue code: easy to write, simple to run, high impact.`}</p>
    <h3 {...{
      "id": "whats-it-do"
    }}>{`What's it do?`}</h3>
    <p>{`This little function connects Gumroad to Auth0.`}</p>
    <p>{`You make a purchase, Gumroad pings a URL, AWS Lambda runs my code, code creates a new user on Auth0, gives the right permissions, and you get access to the course.`}</p>
    <p><figure parentName="p" {...{
        "className": "gatsby-resp-image-figure",
        "style": {
          "textAlign": "center",
          "fontStyle": "italic"
        }
      }}>{`
    `}<span parentName="figure" {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "800px"
          }
        }}>{`
      `}<a parentName="span" {...{
            "className": "gatsby-resp-image-link",
            "href": "/static/7dde88863b36ee41b2ae290792d56064/5a190/Sketch-of-using-AWS-Lambda-as-a-Gumroad-webhook2eee37.png",
            "style": {
              "display": "block"
            },
            "target": "_blank",
            "rel": "noopener"
          }}>{`
    `}<span parentName="a" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "73.54260089686098%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'294\\'%20viewBox=\\'0%200%20400%20294\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M222%2037c-8%201-22%208-28%2011-7%205-14%2017-16%2027l-1%205h-4c-18%201-43%206-43%208l10-2c9-3%2024-5%2033-5h4v7c0%2032%2025%2055%2049%2045%2013-6%2027-19%2037-33l7-7v-1c-2-3%201-4%2025-6%2016-1%2019-1%2018-2a353%20353%200%2000-42%204c-1-1%204-9%205-10l27-4c2%200%202%201-1%203-4%202-5%204-3%204l2-2%208-5c10-5%209-6-6-6-6%200-9%200-8%201l9%201%209%201-4%201-18%202-15%203-1-9c0-6-1-9-2-10-17-20-32-26-51-21m7%201l-7%202-6%201%205%202c4%201%207%205%203%204-9-1-17-1-20%201l-5%202c-4%200-13%2011-17%2021-1%203-1%203%205%205%2014%204%2015%209%202%2011-9%201-9%201-9%205%200%2010%206%2024%2013%2031%2016%2016%2036%2012%2058-11%207-8%2015-19%2014-20l-15%201-1-1%204-3%205-4h4l-3%202-3%202h5l6-1c2-1%204-8%204-9-1%200-19%203-23%205h-5c1-2%2010-4%2019-5%2010-1%2010-2%2010-8%200-8-4-13-8-9-2%201-2%201-2-1s0-2%203-2c5%201%205-1%200-6-4-5-12-10-18-12-4-2-17-3-18-3m-80%2029l-1%203c0%202%200%202%202%201%201-2%201-2%201%200%200%203%201%203%204%205%206%202%2011-1%207-5v-1l1-1c0-1-5-2-8-1h-4l-2-1m-38%2014a1073%201073%200%2001-91%203l-3%206-1%207c-1%203%200%203%2018%202a574%20574%200%20010-2l-16%201%201-4c1-9-1-8%2028-10l46-1%2019%201v8c0%2010%202%2011%202%201s-1-14-3-12m235%203c-2%203-2%207%200%207l15-1h7l-3-1h-2l2-2c3-3%203-4-1-2h-3c-2-2-6-1-6%201-1%203-4%203-4%200-1-3-3-4-5-2M24%20105c-3%200-4%201-4%202l-2%206-2%206%202%201h5c10-4%2033-6%2033-3l1%201%201-4v-5c3-1%200-2-6-3l-10-1H24m1%202c-3%200-4%201-5%206-2%206-1%206%203%205%204-2%2015-4%2024-5%207%200%2011-2%209-4s-25-3-31-2m40%200v5c-1%205-1%206%202%206a938%20938%200%200126-2c-4%200-4%200-3-2%202-7%202-7-13-7H65m2%203v3c0%202%201%203%2010%203h10l1-3%201-4c0-1-21-1-22%201m-31%2011h-3c-2%201-3%201-3%207%200%208%200%208%2011%209l15%203c7%202%2021%201%2022-1v-12c-2-3-2-4-7-5s-32-2-35-1m-2%203l-2%205c-1%205%200%205%2012%207l12%202h20c2-1%201-11-1-13s-40-3-41-1m125%2059l-2%2013%2017%203c2-1%203-13%202-14l-5-1-8-1h-4m1%203l-1%205c0%204%200%204%205%205%209%202%2010%202%2010-5%201-6%201-6-7-6-5-1-6%200-7%201m52%2023l-22%201c-16%200-21%201-18%202%202%202%200%2028-2%2030a358%20358%200%200148-5h11v2l1%203%201-3%201-3v-8c1-5-1-16-3-18-1-1-10-2-17-1m-7%202a233%20233%200%2001-30%202%20635%20635%200%2000-1%2024l-1%204h3a440%20440%200%200153-6l1-7c1-6-1-15-3-17h-22\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="a">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/7dde88863b36ee41b2ae290792d56064/ca0a1/Sketch-of-using-AWS-Lambda-as-a-Gumroad-webhook2eee37.webp 223w", "/static/7dde88863b36ee41b2ae290792d56064/75680/Sketch-of-using-AWS-Lambda-as-a-Gumroad-webhook2eee37.webp 445w", "/static/7dde88863b36ee41b2ae290792d56064/d00b9/Sketch-of-using-AWS-Lambda-as-a-Gumroad-webhook2eee37.webp 800w"],
                "sizes": "(max-width: 800px) 100vw, 800px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/7dde88863b36ee41b2ae290792d56064/e92b6/Sketch-of-using-AWS-Lambda-as-a-Gumroad-webhook2eee37.png 223w", "/static/7dde88863b36ee41b2ae290792d56064/e66bf/Sketch-of-using-AWS-Lambda-as-a-Gumroad-webhook2eee37.png 445w", "/static/7dde88863b36ee41b2ae290792d56064/5a190/Sketch-of-using-AWS-Lambda-as-a-Gumroad-webhook2eee37.png 800w"],
                "sizes": "(max-width: 800px) 100vw, 800px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/7dde88863b36ee41b2ae290792d56064/5a190/Sketch-of-using-AWS-Lambda-as-a-Gumroad-webhook2eee37.png",
                "alt": "Sketch of using AWS Lambda as a Gumroad webhook",
                "title": "Sketch of using AWS Lambda as a Gumroad webhook",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
  `}</a>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
          "className": "gatsby-resp-image-figcaption"
        }}>{`Sketch of using AWS Lambda as a Gumroad webhook`}</figcaption>{`
  `}</figure></p>
    <p>{`Works great. Changed it once in 8 months to add new products. ✌️`}</p>
    <p>{`Oh yeah, products are hardcoded in a dictionary. No need to get fancy, nobody's using this code but me.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-typescript"
      }}>{`const PRODUCTS: { [key: string]: string } = {
  "https://gum.co/IeDvq": "rol_uhLv74dIEcoIExRn", //SRD Professional
  "https://gum.co/WTeMS": "rol_yOuRBy7Yaw50bwb9", //SRD Indie Hacker
  "https://gumroad.com/l/IeDvq": "rol_uhLv74dIEcoIExRn", //SRD Professional
  "https://gumroad.com/l/WTeMS": "rol_yOuRBy7Yaw50bwb9", //SRD Indie Hacker
  "https://gum.co/Fqwwi": "rol_zpnkaVy2LcZtaKZL", //RDV_Basics,
  "https://gum.co/KDLxE": "rol_j0hosj3n5mFTwkhu", //RDV_Full,
  "https://gum.co/Hnbtz": "rol_j0hosj3n5mFTwkhu", //RDV All extras,
  "https://gumroad.com/l/Fqwwi": "rol_zpnkaVy2LcZtaKZL", //RDV_Basics
  "https://gumroad.com/l/KDLxE": "rol_j0hosj3n5mFTwkhu", //RDV_Full
  "https://gumroad.com/l/Hnbtz": "rol_E0XpXwtbt1wezjIS", //RDV All extras

  "https://gum.co/fKfYf": "rol_2Ipay6fbQzcWZ59A", // FindYourIdea
  "https://gumroad.com/l/fKfYf": "rol_2Ipay6fbQzcWZ59A", // FindYourIdea
};
`}</code></pre>
    <p>{`You can read more details in `}<a parentName="p" {...{
        "href": "https://swizec.com/blog/connecting-gumroad-to-auth0-for-paywalled-jamstack-apps/"
      }}>{`Connecting Gumroad to Auth0 for paywalled JAMStack apps`}</a></p>
    <h2 {...{
      "id": "why-this-code-is-great"
    }}>{`Why this code is great`}</h2>
    <p>{`What I like about this code is that it's a JavaScript function. Nothing more, nothing less.`}</p>
    <p>{`Sure it runs on the web, handles API requests, lives in the cloud, has its own URL, runs on-demand, wakes up as a full server, scales up to 1000 simultaneous requests, handles errors, uses distributed logging, `}<em parentName="p">{`and`}</em>{` has basic monitoring built-in ...`}</p>
    <p>{`... but I didn't have to think about any of that. All I did was:`}</p>
    <pre><code parentName="pre" {...{}}>{`serverless deploy
`}</code></pre>
    <p>{`And the platform handled the rest 😍`}</p>
    <p>{`What's your favorite piece of code my friend? hit reply`}</p>
    <p>{`Cheers,`}<br />{`
~Swizec`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      